/**
 * @fileoverview System-wide address-validation breakouts: Mapbox as-you-type
 *   candidate suggestions (interactive layer) + USPS standardization (postal
 *   truth layer), with an offline/Mapbox-only fallback.
 * @module core-domain/forms/shared/addressValidation
 *
 * Responsibilities:
 * - `suggestAddressCandidates`: query Mapbox for ranked, structured candidates.
 * - `parseMapboxFeatures`: pure transform of a Mapbox response into candidates.
 * - `standardizeAddress`: send a chosen candidate to the app's USPS breakout
 *   (`POST {apiBaseUrl}/validate/address`) for canonical USPS formatting.
 * - Degrade gracefully: any network failure falls back to the local
 *   `evaluatePostalAddressFormat` result so saving is never blocked.
 * Dependencies:
 * - `../../types/postalAddress` for the canonical shape + offline validator.
 * - `fetch` (injectable for tests) — same online-breakout precedent as
 *   `validateEmailMx` in this package.
 * Architecture:
 * - Online breakout behind the host's Dexie-first service: callers invoke these
 *   only on explicit user typeahead/validate actions, then persist the chosen
 *   `PostalAddress` to Dexie. The Mapbox token + API base URL are injected by
 *   the host (never hardcoded) so this stays environment-agnostic.
 * Notes:
 * - Mapbox token is a public client token; USPS OAuth creds live server-side
 *   only, reached exclusively through the app's own `/validate/address` route.
 */

import {
  PostalAddress,
  evaluatePostalAddressFormat,
  formatPostalAddress,
} from '../../types/postalAddress';

/** Third-party geocoder base (external API; token injected, never hardcoded). */
const MAPBOX_GEOCODE_BASE = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

/** Minimum query length before hitting Mapbox (matches existing AddressAutofill). */
export const ADDRESS_SUGGEST_MIN_LENGTH = 3;

type FetchLike = (input: string, init?: RequestInit) => Promise<{
  ok: boolean;
  json: () => Promise<unknown>;
}>;

/**
 * @contract AddressCandidate
 * Purpose: A single ranked, structured match returned by the interactive picker.
 * Fields:
 * - id: Stable provider id for list keys/selection.
 * - label: Human-readable full address (Mapbox place_name).
 * - address: Structured PostalAddress parsed from the candidate (status
 *   'needsValidation' until USPS-confirmed).
 * Rules: `address` always carries the four mail-required parts when the provider
 *   supplies them; missing parts remain empty strings.
 * Sync: Transient; not persisted (only the chosen, standardized address is).
 */
export interface AddressCandidate {
  id: string;
  label: string;
  address: PostalAddress;
}

/**
 * @contract AddressProximity
 * Purpose: A soft bias point (never a hard filter) for ranking Mapbox address
 *   suggestions closer to the department's coverage area.
 * Fields:  longitude/latitude — WGS84 degrees, typically the tenant's
 *   `boundaryCentroid`.
 * Rules:   Biasing only — Mapbox still returns exact/high-confidence matches
 *   anywhere; this just re-orders ties toward the given point instead of
 *   excluding out-of-area addresses (a genuine contact may live outside the
 *   district boundary, e.g. a property owner's mailing address).
 */
export interface AddressProximity {
  longitude: number;
  latitude: number;
}

export interface SuggestOptions {
  mapboxToken: string;
  limit?: number;
  /**
   * Optional bias point (e.g. the tenant's `boundaryCentroid`) so suggestions
   * near the department's coverage area rank above equally-valid distant
   * matches — Mapbox's `proximity` param. Omit to fall back to Mapbox's
   * default (IP-based) ranking.
   */
  proximity?: AddressProximity;
  /** Test seam; defaults to global fetch. */
  fetchImpl?: FetchLike;
}

export interface StandardizeOptions {
  /** App API base (e.g. '/api'); the USPS breakout is POST `${apiBaseUrl}/validate/address`. */
  apiBaseUrl: string;
  /** Test seam; defaults to global fetch. */
  fetchImpl?: FetchLike;
}

interface MapboxContextEntry {
  id?: string;
  text?: string;
  short_code?: string;
}

interface MapboxFeature {
  id?: string;
  place_name?: string;
  text?: string;
  address?: string;
  properties?: { address?: string };
  center?: [number, number];
  context?: MapboxContextEntry[];
}

function contextValue(context: MapboxContextEntry[] | undefined, prefix: string): MapboxContextEntry | undefined {
  return context?.find((entry) => typeof entry.id === 'string' && entry.id.startsWith(prefix));
}

/**
 * @process parseMapboxFeatures
 * Steps:
 * 1. For each feature, build line1 from house number (`address`/properties) +
 *    street (`text`).
 * 2. Extract city (context `place`), state (context `region` short_code, drop
 *    the `US-` prefix), ZIP (context `postcode`), country (context `country`).
 * 3. Normalize via `evaluatePostalAddressFormat` and attach geocode lat/lng.
 * Inputs: Raw Mapbox geocoding JSON (unknown-typed for safety).
 * Outputs: Array of AddressCandidate (empty when no features).
 * Failure Handling: Pure; tolerates missing fields without throwing.
 * Idempotency: REQUIRED (pure function).
 */
export function parseMapboxFeatures(raw: unknown): AddressCandidate[] {
  const features = (raw as { features?: MapboxFeature[] } | null)?.features;
  if (!Array.isArray(features)) return [];

  return features.map((feature, index): AddressCandidate => {
    const houseNumber = (feature.address ?? feature.properties?.address ?? '').toString().trim();
    const street = (feature.text ?? '').trim();
    const line1 = [houseNumber, street].filter(Boolean).join(' ').trim();

    const city = contextValue(feature.context, 'place')?.text ?? '';
    const regionEntry = contextValue(feature.context, 'region');
    const state = (regionEntry?.short_code ?? '').replace(/^US-/i, '').toUpperCase() || (regionEntry?.text ?? '');
    const postalCode = contextValue(feature.context, 'postcode')?.text ?? '';
    const countryCode = (contextValue(feature.context, 'country')?.short_code ?? 'US').toUpperCase();

    const [longitude, latitude] = Array.isArray(feature.center) ? feature.center : [undefined, undefined];

    const address = evaluatePostalAddressFormat({
      line1,
      city,
      state,
      postalCode,
      countryCode,
      latitude,
      longitude,
    }) ?? evaluatePostalAddressFormat({ line1: line1 || (feature.place_name ?? '') })!;

    return {
      id: feature.id ?? `mapbox-${index}`,
      label: feature.place_name ?? formatPostalAddress(address),
      address,
    };
  });
}

/**
 * @process suggestAddressCandidates
 * Steps:
 * 1. Short-circuit (return []) when the query is too short or no token.
 * 2. Query Mapbox (US, address types, autocomplete, optionally biased by
 *    `proximity`) and parse features.
 * Inputs: Free-text query + SuggestOptions (token, limit, proximity, fetch seam).
 * Outputs: Promise of AddressCandidate[] (empty on any failure).
 * Failure Handling: Network/parse errors resolve to [] — never throws.
 * Idempotency: NOT REQUIRED (network read; safe to retry).
 */
export async function suggestAddressCandidates(query: string, options: SuggestOptions): Promise<AddressCandidate[]> {
  const trimmed = query.trim();
  if (trimmed.length < ADDRESS_SUGGEST_MIN_LENGTH || !options.mapboxToken) return [];

  const doFetch: FetchLike = options.fetchImpl ?? ((input, init) => fetch(input, init) as unknown as ReturnType<FetchLike>);
  const limit = options.limit ?? 5;
  const proximityParam = options.proximity
    ? `&proximity=${encodeURIComponent(`${options.proximity.longitude},${options.proximity.latitude}`)}`
    : '';
  const url =
    `${MAPBOX_GEOCODE_BASE}/${encodeURIComponent(trimmed)}.json` +
    `?access_token=${encodeURIComponent(options.mapboxToken)}&country=us&types=address&autocomplete=true&limit=${limit}${proximityParam}`;

  try {
    const res = await doFetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return parseMapboxFeatures(data);
  } catch {
    return [];
  }
}

/**
 * @process standardizeAddress
 * Steps:
 * 1. POST the chosen address to the app's USPS breakout `/validate/address`.
 * 2. On success, return the server's standardized PostalAddress.
 * 3. On any failure (offline, USPS down, missing creds), return the local
 *    `evaluatePostalAddressFormat` result so the user can still save.
 * Inputs: A (Mapbox-picked or hand-typed) address + StandardizeOptions.
 * Outputs: Promise of PostalAddress (always resolves, never throws).
 * Failure Handling: Falls back to the offline format result.
 * Idempotency: NOT REQUIRED (network call; safe to retry).
 */
export async function standardizeAddress(
  address: Partial<PostalAddress>,
  options: StandardizeOptions,
): Promise<PostalAddress> {
  const localResult = evaluatePostalAddressFormat(address) ?? evaluatePostalAddressFormat({ line1: '' })!;
  const doFetch: FetchLike = options.fetchImpl ?? ((input, init) => fetch(input, init) as unknown as ReturnType<FetchLike>);

  try {
    const res = await doFetch(`${options.apiBaseUrl}/validate/address`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line1: localResult.line1,
        line2: localResult.line2,
        city: localResult.city,
        state: localResult.state,
        postalCode: localResult.postalCode,
        countryCode: localResult.countryCode,
      }),
    });
    if (!res.ok) return localResult;
    const data = (await res.json()) as { address?: PostalAddress } | null;
    if (data?.address) {
      return { ...data.address, latitude: localResult.latitude, longitude: localResult.longitude };
    }
    return localResult;
  } catch {
    return localResult;
  }
}
